import { clsx, type ClassValue } from "clsx";
import { cubicOut } from "svelte/easing";
import type { TransitionConfig } from "svelte/transition";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

type FlyAndScaleParams = {
    y?: number;
    x?: number;
    start?: number;
    duration?: number;
};

export const flyAndScale = (
    node: Element,
    params: FlyAndScaleParams = { y: -8, x: 0, start: 0.95, duration: 150 }
): TransitionConfig => {
    const style = getComputedStyle(node);
    const transform = style.transform === "none" ? "" : style.transform;

    const scaleConversion = (
        valueA: number,
        scaleA: [number, number],
        scaleB: [number, number]
    ) => {
        const [minA, maxA] = scaleA;
        const [minB, maxB] = scaleB;

        const percentage = (valueA - minA) / (maxA - minA);
        const valueB = percentage * (maxB - minB) + minB;

        return valueB;
    };

    const styleToString = (style: Record<string, number | string | undefined>): string => {
        return Object.keys(style).reduce((str, key) => {
            if (style[key] === undefined) return str;
            return str + `${key}:${style[key]};`;
        }, "");
    };

    return {
        duration: params.duration ?? 200,
        delay: 0,
        css: (t) => {
            const y = scaleConversion(t, [0, 1], [params.y ?? 5, 0]);
            const x = scaleConversion(t, [0, 1], [params.x ?? 0, 0]);
            const scale = scaleConversion(t, [0, 1], [params.start ?? 0.95, 1]);

            return styleToString({
                transform: `${transform} translate3d(${x}px, ${y}px, 0) scale(${scale})`,
                opacity: t,
            });
        },
        easing: cubicOut,
    };
};

export function formatDate(date: string) {
    try {
        // convert from YYYY-MM-DDTHH:MM:SS to HH:MM DD/MM/YYYY
        const [datePart, timePart] = date.split("T");
        const [year, month, day] = datePart.split("-");
        const [hour, minute] = timePart.split(":");
        return `${hour}:${minute} ${day}/${month}/${year}`;
    } catch (error) {
        console.error("Error formatting date", error);
        return date;
    }
}

export function isValidEmail(email: string): boolean {
    // Trim whitespace from both ends
    email = email.trim();

    // Check basic structure
    const parts = email.split("@");
    if (parts.length !== 2) return false;

    const [local, domain] = parts;

    // Check local part
    if (local.length === 0 || local.length > 64) return false;
    if (!/^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+$/.test(local)) return false;
    if (/^[.-]|[.-]$/.test(local)) return false;

    // Check domain part
    if (domain.length === 0 || domain.length > 255) return false;
    const domainParts = domain.split(".");
    if (domainParts.length < 2) return false;
    for (const part of domainParts) {
        if (part.length === 0 || part.length > 63) return false;
        if (!/^[a-zA-Z0-9-]+$/.test(part)) return false;
        if (/^-|-$/.test(part)) return false;
    }

    return true;
}
